/* eslint-disable @typescript-eslint/no-empty-interface */
import { Injectable } from '@angular/core';
import { JSONRPCService } from '@s8l/client-tree-lib';

import { serviceURL } from './service';

export interface MailRole {
  role?: string;
  sender?: string;
  subject?: string;
  content?: string;
  style?: string;
  template?: string;
}

export interface QuizGameType {
  name?: string;
  randomize?: boolean;
  attempts?: number;
  shuffle?: boolean;
  repeat?: boolean;
  timelimit?: boolean;
  eliminate?: boolean;
  answerset?: QuizEnum;
  score?: number;
}

export type QuizEnum = any;

export interface QuizPruneRequest {
  dryRun?: boolean;
  path?: string;
  language?: string;
}

export interface QuizPathRequest {
  path?: string;
  language?: string;
}

export interface QuizRoundModel {
  uuid?: string;
  questions?: number;
  time?: number;
  enabled?: boolean;
  meta?: any;
  modes?: string[];
}

export interface QuizUpdateRequest {
  enabled?: boolean;
  meta?: any;
  modes?: string[];
  path?: string;
  language?: string;
}

export interface QuizCreateRequest {
  enabled?: boolean;
  modes?: string[];
  path?: string;
  language?: string;
}

export interface QuizDeleteRequest {
  path?: string;
  language?: string;
}

export interface QuizSyncFlashcardsRequest {
  path?: string;
  language?: string;
  delete?: boolean;
  enqueue?: boolean;
}

export interface Survey {
  uuid?: string;
  title?: string;
  setup?: SurveySetup;
  start?: string | number;
  end?: string | number;
  evaluations?: number;
}

export interface SurveySetup {
  subtitle?: string;
  entries?: SurveyEntry[];
}

export interface SurveyEntry {
  type?: SurveyEntryType;
  input?: SurveyEntryInputType;
  text?: string;
  config?: SurveyEntryConfig;
  result?: string;
}

export interface SurveyEntryConfig {
  min?: number; // number only
  max?: number; // number only
  decimal?: number; // number only
  options?: string[]; // scale only
}

export enum SurveyEntryType {
  Header = 'header',
  Input = 'input'
}

export enum SurveyEntryInputType {
  Text = 'text',
  Number = 'number',
  Scale = 'scale'
}

export interface SurveyResultsRequest {
  survey?: string;
  offset?: number;
  limit?: number;
}

export interface SurveyResult {
  userdata?: any;
  payload?: SurveySetup;
  timestamp?: string | number;
}

export interface AppConfigRequest {
  name: string;
  short_name: string;
  app: { colors: any };
  wiki: {
    colors: any;
  };
}

export interface TreeConfigRequest {
  depth: number;
  config: any;
}

export interface MediaMediaGetRequest {
  attachment?: boolean;
  bypassCache?: boolean;
  uuid?: string;
  path?: string;
}

export interface MediaMediaPresignedUuidResponse {
  uuid?: string;
  url?: string;
}

export interface MediaMediaPutRequest {
  type?: string;
  uuid?: string;
  path?: string;
}

export interface MediaMediaPutPublicRequest {
  filename?: string;
}

export interface MediaMediaPresignedUrlResponse {
  url?: string;
}

export interface MediaMediaListRequest {
  recursive?: boolean;
  types?: string[];
  path?: string;
}

export interface MediaMediaFileItem {
  uuid?: string;
  path?: string;
  type?: string;
}

export interface MediaMediaPathRequest {
  path?: string;
}

export interface MediaMediaObjectInfoResponse {
  etag?: string;
  uuid?: string;
  path?: string;
  type?: string;
}

export interface WikiSearchRequest {
  query?: string;
  path?: string;
  language?: string;
}

export interface WikiWikiSearchEntry {
  score?: number;
  result?: any;
}

export interface WikiPathRequest {
  path?: string;
  language?: string;
}

export interface WikiPdfExportResponse {
  exported?: boolean;
}

export interface WikiUpdateNodeRequest {
  permission?: number;
  path?: string;
  language?: string;
}

export interface WikiCreateNodeRequest {
  fields?: WikiWikiField[];
  path?: string;
  language?: string;
}

export interface WikiWikiField {
  name?: string;
  type?: string;
  content?: string;
}

export interface QuestionPathRequest {
  path?: string;
  language?: string;
}

export interface QuestionQuestionModel {
  uuid?: string;
  metric?: number;
  answer_time?: number;
  difficulty?: string;
  text?: string;
  image?: string;
  meta?: any;
  answers?: QuestionAnswerModel[];
}

export interface QuestionAnswerModel {
  uuid?: string;
  metric?: number;
  text?: string;
  image?: string;
  correct?: boolean;
}

export interface QuestionUpdateRequest {
  uuid?: string;
  metric?: number;
  answer_time?: number;
  difficulty?: string;
  text?: string;
  image?: string;
  meta?: any;
  answers?: QuestionAnswer[];
  path?: string;
  language?: string;
}

export interface QuestionAnswer {
  uuid?: string;
  metric?: number;
  text?: string;
  image?: string;
  correct?: boolean;
}

export interface QuestionCreateRequest {
  uuid?: string;
  metric?: number;
  answer_time?: number;
  difficulty?: string;
  text?: string;
  image?: string;
  meta?: any;
  answers?: QuestionAnswer[];
  path?: string;
  language?: string;
}

export interface QuestionDeleteRequest {
  uuid?: string;
  path?: string;
  language?: string;
}

export interface QuestionCreateBulkRequest {
  questions?: QuestionQuestion[];
  path?: string;
  language?: string;
}

export interface QuestionQuestion {
  uuid?: string;
  metric?: number;
  answer_time?: number;
  difficulty?: string;
  text?: string;
  image?: string;
  meta?: any;
  answers?: QuestionAnswer[];
}

export interface NewsNewsMetaRequest {
  url?: string;
}

export type NewsNewsMetaResponse = any;

export interface NewsNewsImagesResponse {
  images?: string[];
}

export interface TreeLinkRequest {
  reference?: string;
  path?: string;
  language?: string;
}

export interface TreeUpdateRequest {
  uuid?: string;
  meta?: any;
  permissions?: any;
  metric?: number;
  path?: string;
  language?: string;
}

export interface TreeCmsSegment {
  uuid?: string;
  path?: TreePath;
  type?: TreeEnum;
  generation?: number;
  metric?: number;
  name?: string;
  names?: any;
  reference?: string;
  children?: TreeCmsSegment[];
  meta?: TreeMetadata;
  resources?: TreeEnum[];
  permissions?: any;
}

export type TreePath = any;

export type TreeEnum = any;

export interface TreeMetadata {
  sidemenu?: boolean;
  glossary?: boolean;
  author?: string;
  icon?: string;
  wikiLink?: string;
  color?: string;
  images?: string[];
  mobileImages?: string[];
  imageAnchors?: string[];
  mobileAnchors?: string[];
  mediafiles?: string[];
}

export interface TreeUpdateResourceRequest {
  chat?: boolean;
  path?: string;
  language?: string;
}

export interface TreeReferenceByUUIDRequest {
  target?: string;
  path?: string;
  language?: string;
}

export interface TreeDeleteRequest {
  recursive?: boolean;
  path?: string;
  language?: string;
}

export interface TreeTranslateRequest {
  reference?: string;
  path?: string;
  language?: string;
  recursive?: boolean;
}

export interface TreeReferenceRequest {
  path?: string; // parent of the ref node
  target?: string; // where the ref node points to
  metric?: number;
  language?: string;
}

export interface TreeRenameRequest {
  name?: string;
  path?: string;
  language?: string;
}

export interface TreeFindReferencesRequest {
  path?: string;
  language?: string;
}

export interface TreeFindReferencesResponse {
  direct?: string[];
  indirect?: string[];
}

export interface TreeGetRequest {
  language?: string;
  full?: boolean;
  depth?: number;
  parentUuid?: string;
  // Takes priority over parentUuid server-side - a uuid alone can't identify
  // a specific reference node's own occurrence in the tree.
  path?: string;
}

export interface TreeTreeResponse {
  root?: TreeCmsSegment;
  generation?: number;
  permissions?: any;
}

export interface TreeCloneRequest {
  newPath?: string;
  path?: string;
  language?: string;
}

export interface TreeMoveRequest {
  newPath?: string;
  path?: string;
  language?: string;
}

export interface TreePermissionRecursiveRequest {
  path?: string;
  language?: string;
  role: string;
  permission: string[];
  add: boolean;
}

export interface TreePermissionRecursiveResponse {
  changed: number;
  total: number;
}

export interface TreeCreateRequest {
  uuid?: string;
  meta?: any;
  permissions?: any;
  metric?: number;
  path?: string;
  language?: string;
}

export interface VimeoMoveRequest {
  folder?: string;
  video?: string;
}

export interface VimeoAuthRequest {
  code?: string;
  state?: string;
}

export interface VimeoUploadRequest {
  filename?: string;
  size?: string;
}

export interface VimeoUploadResponse {
  url?: string;
}

export interface VimeoUpdateRequest {
  name?: string;
  video?: string;
}

export interface VimeoFolderResponseEntry {
  name?: string;
  id?: string;
}

export interface VimeoVideoRequest {
  video?: string;
}

export interface VimeoListResponseEntry {
  name?: string;
  path?: VimeoPath;
  id?: string;
  url?: string;
  image?: string;
  transcode?: string;
  domains?: string[];
}

export type VimeoPath = any;

export interface VimeoThumbnailUrlResponse {
  picture_url?: string;
  upload_url?: string;
}

export interface VimeoFolderRequest {
  folder?: string;
}

export interface VimeoThumbnailSetRequest {
  picture_url?: string;
}

export interface VimeoStatusResponse {
  logged_in?: boolean;
  account_name?: string;
  account_type?: string;
}

export type Instant = any;

export interface Achievement {
  uuid?: string;
  name?: string;
  type?: string;
  parent?: string;
  children?: Achievement[];
  metric?: number;
  display?: AchievementDisplay;
  stat?: AchievementStat;
  i18n_locked?: any;
  i18n_achieved?: any;
  effects?: (AchievementNotificationEffect | AchievementScoreEffect | AchievementStatEffect)[];
  filters?: AchievementFilter[];
  unlock_value?: number;
  repeatable?: boolean;
  event_start?: Instant;
  event_end?: Instant;
  meta?: any;
}

export enum AchievementDisplay {
  Show = 'show',
  Secret = 'secret',
  Event = 'event',
  Hidden = 'hidden'
}

export interface AchievementStat {
  uuid?: string;
  event?: string;
  selector?: string;
  context?: string;
  min_value?: number;
  max_value?: number;
}

export interface AchievementNotificationEffect {
  type?: string;
  value?: any;
}

export interface AchievementScoreEffect {
  type?: string;
  value?: number;
}

export interface AchievementStatEffect {
  type?: string;
  event?: string;
  selector?: string;
  value?: number;
  increment?: boolean;
}

export interface AchievementFilter {
  key?: string;
  value?: string;
  positive?: boolean;
}

export interface StatUserLabel {
  key?: string;
  values?: string[];
}

//currently only for type: review
export interface UpdateGameModeParams {
  type: 'review';
  maxAttempts?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CmsServerService extends JSONRPCService {
  public async mailSendToRole(params: MailRole): Promise<string[]> {
    return this.message(serviceURL('CMS_SERVER', '/mail'), 'send_to_role', params);
  }

  public async quizModes(): Promise<QuizGameType[]> {
    return this.message(serviceURL('CMS_SERVER', '/quiz'), 'modes', null);
  }

  public async quizPrune(params: QuizPruneRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/quiz'), 'prune', params);
  }

  public async quizGet(params: QuizPathRequest): Promise<QuizRoundModel> {
    return this.message(serviceURL('CMS_SERVER', '/quiz'), 'get', params);
  }

  public async quizUpdate(params: QuizUpdateRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/quiz'), 'update', params);
  }

  public async quizCreate(params: QuizCreateRequest): Promise<QuizRoundModel> {
    return this.message(serviceURL('CMS_SERVER', '/quiz'), 'create', params);
  }

  public async quizDelete(params: QuizDeleteRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/quiz'), 'delete', params);
  }

  public async quizSyncFlashcards(params: QuizSyncFlashcardsRequest): Promise<number> {
    return this.message(serviceURL('CMS_SERVER', '/quiz'), 'sync-flashcards', params);
  }

  public async surveyEvaluations(params: SurveyResultsRequest): Promise<SurveyResult[]> {
    return this.message(serviceURL('CMS_SERVER', '/survey'), 'evaluations', params);
  }

  public async surveyGet(params: string): Promise<Survey> {
    return this.message(serviceURL('CMS_SERVER', '/survey'), 'get', params);
  }

  public async surveyUpdate(params: Survey): Promise<Survey> {
    return this.message(serviceURL('CMS_SERVER', '/survey'), 'update', params);
  }

  public async surveyCreate(params: Survey): Promise<Survey> {
    return this.message(serviceURL('CMS_SERVER', '/survey'), 'create', params);
  }

  public async surveyList(): Promise<Survey[]> {
    return this.message(serviceURL('CMS_SERVER', '/survey'), 'list', null);
  }

  public async surveyDelete(params: string): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/survey'), 'delete', params);
  }

  public async configUpdateApp(params: AppConfigRequest): Promise<AppConfigRequest> {
    return this.message(serviceURL('CMS_SERVER', '/config'), 'update-app', params);
  }

  public async configUpdateTree(params: TreeConfigRequest): Promise<any> {
    return this.message(serviceURL('CMS_SERVER', '/config'), 'update-tree', params);
  }

  public async updatePing(): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/update'), 'ping', null);
  }

  public async mediaDownload(params: MediaMediaGetRequest): Promise<MediaMediaPresignedUuidResponse> {
    return this.message(serviceURL('CMS_SERVER', '/media'), 'download', params);
  }

  public async mediaUpload(params: MediaMediaPutRequest): Promise<MediaMediaPresignedUrlResponse> {
    return this.message(serviceURL('CMS_SERVER', '/media'), 'upload', params);
  }

  public async mediaPublicUpload(params: MediaMediaPutPublicRequest): Promise<MediaMediaPresignedUrlResponse> {
    return this.message(serviceURL('CMS_SERVER', '/media'), 'upload-public', params);
  }

  public async mediaList(params: MediaMediaListRequest): Promise<MediaMediaFileItem[]> {
    return this.message(serviceURL('CMS_SERVER', '/media'), 'list', params);
  }

  public async mediaDelete(params: MediaMediaPathRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/media'), 'delete', params);
  }

  public async mediaInfo(params: MediaMediaPathRequest): Promise<MediaMediaObjectInfoResponse> {
    return this.message(serviceURL('CMS_SERVER', '/media'), 'info', params);
  }

  public async wikiSearch(params: WikiSearchRequest, type: string): Promise<WikiWikiSearchEntry[]> {
    return this.message(serviceURL('CMS_SERVER', '/wiki/' + type), 'search', params);
  }

  public async wikiPdf(params: WikiPathRequest, type: string): Promise<WikiPdfExportResponse> {
    return this.message(serviceURL('CMS_SERVER', '/wiki/' + type), 'pdf', params);
  }

  public async wikiGet(params: WikiPathRequest, type: string): Promise<any> {
    return this.message(serviceURL('CMS_SERVER', '/wiki/' + type), 'get', params);
  }

  public async wikiUpdate(params: WikiUpdateNodeRequest, type: string): Promise<any> {
    return this.message(serviceURL('CMS_SERVER', '/wiki/' + type), 'update', params);
  }

  public async wikiCreate(params: WikiCreateNodeRequest, type: string): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/wiki/' + type), 'create', params);
  }

  public async wikiRaw(params: WikiPathRequest, type: string): Promise<any> {
    return this.message(serviceURL('CMS_SERVER', '/wiki/' + type), 'raw', params);
  }

  public async wikiDelete(params: WikiPathRequest, type: string): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/wiki/' + type), 'delete', params);
  }

  public async questionGet(params: QuestionPathRequest): Promise<QuestionQuestionModel[]> {
    return this.message(serviceURL('CMS_SERVER', '/quiz/question'), 'get', params);
  }

  public async questionUpdate(params: QuestionUpdateRequest): Promise<string> {
    return this.message(serviceURL('CMS_SERVER', '/quiz/question'), 'update', params);
  }

  public async questionCreate(params: QuestionCreateRequest): Promise<string> {
    return this.message(serviceURL('CMS_SERVER', '/quiz/question'), 'create', params);
  }

  public async questionDelete(params: QuestionDeleteRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/quiz/question'), 'delete', params);
  }

  public async questionCreateBulk(params: QuestionCreateBulkRequest): Promise<string[]> {
    return this.message(serviceURL('CMS_SERVER', '/quiz/question'), 'create-bulk', params);
  }

  public async newsSearch(params: NewsNewsMetaRequest): Promise<NewsNewsMetaResponse> {
    return this.message(serviceURL('CMS_SERVER', '/news'), 'search', params);
  }

  public async newsImages(params: NewsNewsMetaRequest): Promise<NewsNewsImagesResponse> {
    return this.message(serviceURL('CMS_SERVER', '/news'), 'images', params);
  }

  public async treeLink(params: TreeLinkRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'link', params);
  }

  public async treeUpdate(params: TreeUpdateRequest): Promise<TreeCmsSegment> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'update', params);
  }

  public async treeUpdateResource(params: TreeUpdateResourceRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'update-resource', params);
  }

  public async treeReferenceByUUID(params: TreeReferenceByUUIDRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'referenceByUUID', params);
  }

  public async treeDelete(params: TreeDeleteRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'delete', params);
  }

  public async treeTranslate(params: TreeTranslateRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'translate', params);
  }

  public async treeReference(params: TreeReferenceRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'reference', params);
  }

  public async treeRename(params: TreeRenameRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'rename', params);
  }

  public async treeTranslations(): Promise<string[]> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'translations', null);
  }

  public async treeGet(params: TreeGetRequest): Promise<TreeTreeResponse> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'get', params);
  }

  public async treeFindReferences(params: TreeFindReferencesRequest): Promise<TreeFindReferencesResponse> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'find-references', params);
  }

  public async treeClone(params: TreeCloneRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'clone', params);
  }

  public async treeCreate(params: TreeCreateRequest): Promise<TreeCmsSegment> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'create', params);
  }

  public async treeMove(params: TreeMoveRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'move', params);
  }

  public async treeUpdatePermissionRecursive(params: TreePermissionRecursiveRequest): Promise<TreePermissionRecursiveResponse> {
    return this.message(serviceURL('CMS_SERVER', '/tree'), 'update-permission-recursive', params);
  }

  public async vimeoMove(params: VimeoMoveRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'move', params);
  }

  public async vimeoAuth(params: VimeoAuthRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'auth', params);
  }

  public async vimeoUpload(params: VimeoUploadRequest): Promise<VimeoUploadResponse> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'upload', params);
  }

  public async vimeoAuthUrl(): Promise<string> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'auth-url', null);
  }

  public async vimeoUpdate(params: VimeoUpdateRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'update', params);
  }

  public async vimeoListFolder(): Promise<VimeoFolderResponseEntry[]> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'list-folder', null);
  }

  public async vimeoWhitelist(params: VimeoVideoRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'whitelist', params);
  }

  public async vimeoList(): Promise<VimeoListResponseEntry[]> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'list', null);
  }

  public async vimeoDelete(params: VimeoVideoRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'delete', params);
  }

  public async vimeoLogout(): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'logout', null);
  }

  public async vimeoThumbnailUrl(params: VimeoVideoRequest): Promise<VimeoThumbnailUrlResponse> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'thumbnail-url', params);
  }

  public async vimeoDeleteFolder(params: VimeoFolderRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'delete-folder', params);
  }

  public async vimeoThumbnailSet(params: VimeoThumbnailSetRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'thumbnail-set', params);
  }

  public async vimeoCreateFolder(params: VimeoFolderRequest): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'create-folder', params);
  }

  public async vimeoStatus(): Promise<VimeoStatusResponse> {
    return this.message(serviceURL('CMS_SERVER', '/vimeo'), 'status', null);
  }

  public async achievementList(): Promise<Achievement[]> {
    return this.message(serviceURL('CMS_SERVER', '/achievement'), 'list', {});
  }

  public async achievementCreate(params: Achievement): Promise<Achievement> {
    return this.message(serviceURL('CMS_SERVER', '/achievement'), 'create', params);
  }

  public async achievementUpdate(params: Achievement): Promise<Achievement> {
    return this.message(serviceURL('CMS_SERVER', '/achievement'), 'update', params);
  }

  public async achievementUpsert(params: Achievement[]): Promise<Achievement[]> {
    return this.message(serviceURL('CMS_SERVER', '/achievement'), 'upsert', { value: params });
  }

  public async achievementDelete(uuid: string, recursive: boolean, stats: boolean): Promise<any> {
    return this.message(serviceURL('CMS_SERVER', '/achievement'), 'delete', { uuid, recursive, stats });
  }

  public async statsLabelsList(): Promise<StatUserLabel[]> {
    return this.message(serviceURL('CMS_SERVER', '/stats'), 'list-labels', null);
  }

  public async getGameTypeSettings(type: string): Promise<QuizGameType> {
    return this.message(serviceURL('CMS_SERVER', '/gametype'), `get`, { name: type });
  }

  public async updateGameTypeSettings(params: UpdateGameModeParams): Promise<any> {
    return this.message(serviceURL('CMS_SERVER', '/gametype'), `update`, params);
  }

  public async customContentUploadInit(path: string): Promise<{ uploadUrl: string; stagingKey: string }> {
    return this.message(serviceURL('CMS_SERVER', '/custom-content'), 'upload.init', { path });
  }

  public async customContentUploadProcess(nodePath: string, stagingKey: string): Promise<{ fileCount: number; totalBytes: number }> {
    return this.message(serviceURL('CONTENT_UPLOAD_SERVER', '/upload'), 'staging.process', { nodePath, stagingKey });
  }

  public async customContentUploadComplete(path: string): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/custom-content'), 'upload.process', { path });
  }

  public async customContentTokenIssue(path: string): Promise<{ token: string }> {
    return this.message(serviceURL('CMS_SERVER', '/custom-content'), 'token.issue', { path });
  }

  public async customContentDelete(path: string): Promise<void> {
    return this.message(serviceURL('CMS_SERVER', '/custom-content'), 'content.delete', { path });
  }
}
